/**
 * SoleadLab 2.0 - Photo Classification Panel (PFC-1 + PFC-4B multi-fault)
 *
 * Walk the queue of unclassified `type:'Photo'` indications. PFC-4B (Model B):
 * the photo stays a `type:'Photo'` CONTAINER; each fault is a separate CHILD
 * indication created via the existing authorized `projectStore.addIndication`
 * path and linked back through `properties.sourcePhotoId`. Children carry NO
 * `image`/`visualImage` (imagery is resolved through the source photo in view
 * code). The source photo leaves the queue only when `properties.reviewed`.
 *
 * Interaction (the "Add indication" model): the photo opens with ONE selected,
 * draggable fault marker. Click a fault type to set the selected marker's type;
 * "+ Add indication" adds another local marker; when EVERY marker has a type
 * the panel creates all the children, marks the photo reviewed, and advances.
 * Each marker owns its own thermal ROI (per-marker, no bleed). "Undo last
 * marker" drops the most-recently-added local marker (never the original).
 *
 *  - No committed-fault delete; marker undo is pure local state.
 *  - Not available in read-only / share-viewer mode.
 */

import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import type { ReactNode } from 'react';
import mapboxgl from 'mapbox-gl';
import { useProjectStore, useUIStore, useMapStore, INDICATION_TYPES } from '../../stores';
import type { Indication, IndicationType, IndicationProperties, IndicationObservation } from '../../stores';
import { useIsReadOnlyViewer } from '../../hooks/useShareViewer';
import { resolveImageSrc, centroidOfPolygon } from '../../utils';
import { haversineM } from '../../utils/missionValidation';
import { deriveObservations, activeObservation } from '../../utils/observations';
import { captureDay, compareCaptureOrder } from '../../utils/captureDate';
import { parseDriveIdFromUrl, driveThumbnailUrl } from '../../utils/driveImport';
import { PhotoSection } from './IndicationPreview';
import { getPhotoBlobEntry } from '../../utils/photoBlobs';
import { resolveSignedUrls } from '../../services/signedAssetUrl';
import { buildTileUrl } from '../../services/tileApi';
import { ThermalAnnotator, ThermalControls, ThermalStats, useThermalAnnotator } from './thermal';
import type { ThermalAnalysisData } from './thermal';
import { useTopologySuggestion } from '../../domain/topology/useTopologySuggestion';
import { useDerivedFieldsForGeometry } from '../../domain/topology/useDerivedTopology';
import './PhotoClassificationPanel.css';

/** Resolve an image path for the classify panes, upgrading Drive-proxy URLs to
 *  the sized thumbnail variant. Old imports baked the proxy URL without a
 *  width, so rebuilding from the parsed file ID retrofits them too. Non-Drive
 *  paths (R2 assets, local blobs) resolve unchanged. 800px (~250KB, ~20x
 *  smaller than the original) is pixel-perfect at the pane's fit size and
 *  keeps fast browsing cheap; zooming fetches the full original on demand. */
function resolveSizedImageSrc(path: string | undefined): string {
  if (!path) return '';
  const driveId = parseDriveIdFromUrl(path);
  return driveId ? driveThumbnailUrl(driveId, 800) : resolveImageSrc(path);
}

interface PhotoClassificationPanelProps {
  onClose: () => void;
}

/**
 * PFC-5: per-project last-classified-photo position, persisted in localStorage
 * so a new session can resume where the last one left off. Stores both the id
 * and the chronological (capture) date so a removed/classified photo can fall
 * back to the nearest remaining photo.
 */
type SavedResume = { photoId: string; captureDate: string | null };

function pfcResumeKey(siteId: string): string {
  return `pfc:lastPhoto:${siteId}`;
}

/** Read the saved resume marker; tolerant of the legacy bare-id string form. Never throws. */
function readSavedResume(siteId: string | undefined): SavedResume | null {
  if (!siteId) return null;
  let raw: string | null = null;
  try {
    raw = typeof localStorage !== 'undefined' ? localStorage.getItem(pfcResumeKey(siteId)) : null;
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object') {
      const o = parsed as Record<string, unknown>;
      if (typeof o.photoId === 'string') {
        return { photoId: o.photoId, captureDate: typeof o.captureDate === 'string' ? o.captureDate : null };
      }
    }
    if (typeof parsed === 'string') return { photoId: parsed, captureDate: null };
    return null;
  } catch {
    // Legacy value stored as a bare (non-JSON) id string — exact-id resume only.
    return { photoId: raw, captureDate: null };
  }
}

/** Persist the current position; best-effort, never throws/blocks the panel. */
function writeSavedResume(siteId: string | undefined, value: SavedResume): void {
  if (!siteId) return;
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(pfcResumeKey(siteId), JSON.stringify(value));
  } catch {
    /* ignore quota / availability errors */
  }
}

/** The 12 counted fault types — the classification targets. */
const FAULT_TYPES = (
  Object.entries(INDICATION_TYPES) as [
    IndicationType,
    { label: string; color: string; counted: boolean },
  ][]
)
  .filter(([, cfg]) => cfg.counted)
  .map(([key, cfg]) => ({ key, label: cfg.label, color: cfg.color }));

/** Counted fault-type keys — the only indications the "link to existing" proximity
 *  suggestion considers (never Photos or other non-fault types). */
const FAULT_TYPE_KEYS = new Set<IndicationType>(FAULT_TYPES.map((f) => f.key));

/** Proximity thresholds for the classify "link to an existing fault" suggestion. */
const LINK_SUGGEST_RADIUS_M = 30; // only offer a link when a committed fault is this close
const LINK_FAR_WARN_M = 12;       // warn beyond this — probably a different fault

/** Short, human-scannable id tail (fallback when a fault has no topology). */
const shortId = (id: string) => id.slice(-4);

/** Layout location from a fault's topology (Sta/Inv/DCCB/Str/Mod) — identifies
 *  WHERE the fault is, far more useful in the link label than an opaque id tail. */
function faultLocationLabel(ind: Indication): string {
  const p = ind.properties as unknown as Record<string, unknown>;
  const parts: string[] = [];
  for (const f of TOPO_FIELDS) {
    const v = p[f.key];
    if (v) parts.push(`${f.label} ${v}`);
  }
  return parts.join(' · ');
}

/** A topology suggestion's fields → a layout-location label. Fallback for faults
 *  with no STORED topology — derived from the site layout at the fault's position. */
function topoFieldsLabel(fields: unknown): string {
  const f2 = (fields ?? {}) as Record<string, unknown>;
  const parts: string[] = [];
  for (const f of TOPO_FIELDS) {
    const v = f2[f.key];
    if (v) parts.push(`${f.label} ${v}`);
  }
  return parts.join(' · ');
}

/** Editable topology fields shown below the photo (like the indication preview). */
const TOPO_FIELDS = [
  { key: 'station', label: 'Sta' },
  { key: 'inverter', label: 'Inv' },
  { key: 'mppt', label: 'MPPT' },
  { key: 'dccb', label: 'DCCB' },
  { key: 'string', label: 'Str' },
  { key: 'module', label: 'Mod' },
] as const;

type TopoKey = (typeof TOPO_FIELDS)[number]['key'];

/** A local, uncommitted fault marker for the current source photo (PFC-4B). */
interface FaultMarker {
  localId: string;
  geometry: GeoJSON.Geometry;
  type: IndicationType | null;
  topo: Partial<Record<TopoKey, string>>;
  createIssue: boolean;
  /** Cross-inspection link: when set, finishing this marker appends a new
   *  OBSERVATION to this existing fault instead of creating a new child. The
   *  fault's geometry/topology stay put; only evidence (photo/thermal) is added. */
  linkTargetId?: string;
  // Per-marker thermal ROI lives in a ref keyed by localId (see markerThermal),
  // NOT here — so the analyzer's render-phase report never setStates the panel.
}

/**
 * Inclusive `YYYY-MM-DD` range test against `properties.captureDate`.
 * Empty from+to means "all"; a set bound excludes undated photos.
 */
function inDateRange(captureDate: string | undefined, from: string, to: string): boolean {
  if (!from && !to) return true;
  if (!captureDate) return false;
  if (from && captureDate < from) return false;
  if (to && captureDate > to) return false;
  return true;
}

/** Centroid lng/lat of a geometry (Point coords, or polygon centroid). */
function geomCenter(g: GeoJSON.Geometry): [number, number] | null {
  if (g.type === 'Point') return g.coordinates as [number, number];
  if (g.type === 'Polygon') {
    const c = centroidOfPolygon(g.coordinates[0] as [number, number][]);
    return c ?? null;
  }
  return null;
}

/** Representative lng/lat of an indication. */
function getLngLat(ind: Indication): [number, number] | null {
  return geomCenter(ind.geometry);
}

/** Translate a Point/Polygon geometry by a lng/lat delta (marker drag). */
function translateGeometry(geom: GeoJSON.Geometry, dLng: number, dLat: number): GeoJSON.Geometry {
  if (geom.type === 'Point') {
    const [lng, lat] = geom.coordinates as [number, number];
    return { type: 'Point', coordinates: [lng + dLng, lat + dLat] };
  }
  if (geom.type === 'Polygon') {
    const rings = (geom.coordinates as [number, number][][]).map((ring) =>
      ring.map(([lng, lat]) => [lng + dLng, lat + dLat]),
    );
    return { type: 'Polygon', coordinates: rings };
  }
  return geom;
}

function thermalHasContent(data: ThermalAnalysisData | undefined): boolean {
  return !!data && ((data.rois?.length ?? 0) > 0 || (data.points?.length ?? 0) > 0);
}

/* ── Scroll-to-zoom / drag-to-pan frame ──────────────────────────────────── */

interface ZoomPanFrameProps {
  children: ReactNode;
  /** Drag-to-pan when zoomed (visual image only; off for the thermal so the
   *  canvas keeps its pointer events for ROI drawing). */
  enablePan?: boolean;
  /** Resetting this (per photo) returns the view to 1× / no pan. */
  resetKey?: string | number;
  className?: string;
  style?: React.CSSProperties;
  /** Rendered above the (transformed) content, e.g. a loading skeleton. */
  overlay?: ReactNode;
  /** Fires when the view crosses between 1× and zoomed-in — lets the owner
   *  fetch the full-res original on zoom intent. */
  onZoomedChange?: (zoomed: boolean) => void;
}

function ZoomPanFrame({ children, enablePan = false, resetKey, className, style, overlay, onZoomedChange }: ZoomPanFrameProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const stateRef = useRef({ scale: 1, pan: { x: 0, y: 0 } });
  const dragRef = useRef<{ x: number; y: number; px: number; py: number } | null>(null);

  useEffect(() => {
    stateRef.current = { scale, pan };
  }, [scale, pan]);

  // Notify the owner when zoom intent starts/stops (crossing 1×).
  const zoomedRef = useRef(false);
  useEffect(() => {
    const zoomed = scale > 1;
    if (zoomed !== zoomedRef.current) {
      zoomedRef.current = zoomed;
      onZoomedChange?.(zoomed);
    }
  }, [scale, onZoomedChange]);

  // Reset on photo change.
  useEffect(() => {
    setScale(1);
    setPan({ x: 0, y: 0 });
  }, [resetKey]);

  // Native non-passive wheel listener so preventDefault works (React's onWheel
  // is passive and would warn). Reads latest scale/pan via stateRef.
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const { scale: s, pan: p } = stateRef.current;
      const next = Math.min(8, Math.max(1, s * Math.exp(-e.deltaY * 0.0015)));
      const k = next / s;
      const nx = next === 1 ? 0 : cx - (cx - p.x) * k;
      const ny = next === 1 ? 0 : cy - (cy - p.y) * k;
      setScale(next);
      setPan({ x: nx, y: ny });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!enablePan || stateRef.current.scale <= 1) return;
    dragRef.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    setPan({ x: d.px + (e.clientX - d.x), y: d.py + (e.clientY - d.y) });
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* noop */ }
  };

  const panning = enablePan && scale > 1;
  return (
    <div
      ref={frameRef}
      className={`pcp-zoom${className ? ` ${className}` : ''}`}
      onPointerDown={enablePan ? onPointerDown : undefined}
      onPointerMove={enablePan ? onPointerMove : undefined}
      onPointerUp={enablePan ? onPointerUp : undefined}
      style={{ cursor: panning ? 'grab' : undefined, ...style }}
    >
      <div
        className="pcp-zoom__inner"
        style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`, transformOrigin: '0 0' }}
      >
        {children}
      </div>
      {overlay}
      {scale > 1 && (
        <button
          type="button"
          className="pcp-zoom__reset"
          onClick={() => { setScale(1); setPan({ x: 0, y: 0 }); }}
          title="Reset zoom"
        >
          {scale.toFixed(1)}× · reset
        </button>
      )}
    </div>
  );
}

/* ── Visual image pane: high-priority load, skeleton + error fallback ──────── */

interface VisualPaneProps {
  src: string;
  /** Full-resolution variant to swap in after `src` settles (Drive-proxied
   *  photos browse on the fast sized thumbnail; zoom needs the original).
   *  Empty when `src` already IS full-res (local blobs / R2 assets). */
  fullSrc?: string;
  /** Fires once the current visual settles (load OR error) so the panel can
   *  defer preloading the next photo's visual until bandwidth is free. */
  onSettled: () => void;
}

/** Dwell before the full-res original downloads WITHOUT zoom intent. Long on
 *  purpose: fast browsing must cost thumbnails only — the ~5MB originals are
 *  what clog the browser's connection pool and stall navigation. Zooming
 *  fetches immediately instead. */
const FULL_RES_DWELL_MS = 1500;

function VisualPane({ src, fullSrc, onSettled }: VisualPaneProps) {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [wantFull, setWantFull] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const prevSrc = useRef(src);
  // Render-phase reset the moment the photo (src) changes, so the previous
  // photo's image never flashes as "loaded" under the new src.
  if (prevSrc.current !== src) {
    prevSrc.current = src;
    setStatus('loading');
    setWantFull(false);
    setShowFull(false);
  }
  const settle = (next: 'loaded' | 'error') => { setStatus(next); onSettled(); };

  // Zoom intent → fetch the original NOW (stable callback for ZoomPanFrame).
  const handleZoomedChange = useCallback((zoomed: boolean) => {
    if (zoomed) setWantFull(true);
  }, []);

  // No zoom intent → only after a long dwell on a settled thumbnail.
  useEffect(() => {
    if (!fullSrc || wantFull || status !== 'loaded') return;
    const t = setTimeout(() => setWantFull(true), FULL_RES_DWELL_MS);
    return () => clearTimeout(t);
  }, [fullSrc, wantFull, status, src]);

  // Demand-driven full-res fetch + swap. The swap keeps zoom/pan state
  // (ZoomPanFrame's resetKey stays the thumbnail src). The in-flight download
  // is CANCELED when the user moves on (src = '' aborts the request) so
  // abandoned fetches never pile onto the browser's connection limit.
  useEffect(() => {
    if (!wantFull || !fullSrc || showFull) return;
    let alive = true;
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => { if (alive) setShowFull(true); };
    img.src = fullSrc;
    return () => {
      alive = false;
      img.onload = null;
      img.src = '';
    };
  }, [wantFull, fullSrc, showFull]);

  const displaySrc = showFull && fullSrc ? fullSrc : src;

  const overlay =
    status === 'error' ? (
      <div className="pcp-visual__error">Couldn’t load the visual image</div>
    ) : status === 'loading' ? (
      <div className="pcp-visual__skeleton" aria-hidden="true" />
    ) : null;

  return (
    <ZoomPanFrame enablePan resetKey={src} className="pcp-visual" overlay={overlay} onZoomedChange={handleZoomedChange}>
      <img
        // Keyed by the PHOTO (src prop): switching photos remounts the element
        // so the previous photo never lingers under the skeleton; the full-res
        // swap reuses the element (no blank flash mid-zoom).
        key={src}
        className="pcp-visual__img"
        src={displaySrc}
        alt="Visual"
        draggable={false}
        decoding="async"
        // React 18.3 ignores camelCase `fetchPriority`; pass the lowercase DOM
        // attribute so the browser actually applies the high-priority hint.
        {...({ fetchpriority: 'high' } as unknown as React.ImgHTMLAttributes<HTMLImageElement>)}
        onLoad={() => settle('loaded')}
        onError={() => settle('error')}
      />
    </ZoomPanFrame>
  );
}

/* ── Interactive thermal analyzer (source photo image, ROIs for one marker) ── */

interface PhotoThermalAnalyzerProps {
  /** The marker (or photo) this analyzer's ROIs belong to. */
  indicationId: string;
  imageSrc: string;
  photoRelPath?: string;
  existingThermalData?: ThermalAnalysisData;
  onReport: (id: string, data: ThermalAnalysisData) => void;
}

function PhotoThermalAnalyzer({
  indicationId,
  imageSrc,
  photoRelPath,
  existingThermalData,
  onReport,
}: PhotoThermalAnalyzerProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const cancelledRef = useRef(false);
  const handleChange = useCallback(
    (data: ThermalAnalysisData) => onReport(indicationId, data),
    [onReport, indicationId],
  );
  const annotator = useThermalAnnotator(handleChange);

  useEffect(() => {
    if (existingThermalData) annotator.load(existingThermalData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAnalyze = useCallback(async () => {
    if (!photoRelPath || cancelledRef.current) return;
    const entry = getPhotoBlobEntry(photoRelPath);
    if (entry?.file) {
      if (!cancelledRef.current) annotator.analyze(entry.file);
      return;
    }
    const src = resolveImageSrc(photoRelPath);
    if (!src) return;
    try {
      const resp = await fetch(src, { credentials: 'include' });
      if (!resp.ok) throw new Error(`Asset fetch failed: ${resp.status}`);
      const blob = await resp.blob();
      if (cancelledRef.current) return; // user navigated away during the fetch
      const fileName = photoRelPath.split('/').pop() || 'thermal.jpg';
      annotator.analyze(new File([blob], fileName, { type: blob.type }));
    } catch (err) {
      if (cancelledRef.current) return;
      console.error('[PCP Thermal] Failed to fetch image for analysis:', err);
      useUIStore.getState().addNotification({
        type: 'error',
        title: 'Thermal analysis failed',
        message: 'Could not load image for thermal analysis.',
      });
    }
  }, [photoRelPath, annotator]);

  // Debounced auto-analyze: only start after the user settles ~200ms. Keyed per
  // (photo, selected marker), so switching marker remounts and re-decodes from
  // cache; the cleanup cancels the timer (and ignores any in-flight result).
  useEffect(() => {
    // Reset on (re)mount so StrictMode's mount→cleanup→remount double-invoke
    // doesn't leave the ref stuck `true` and suppress the analysis entirely.
    cancelledRef.current = false;
    const t = setTimeout(() => { handleAnalyze(); }, 200);
    return () => {
      cancelledRef.current = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="pcp-thermal" data-testid="pcp-thermal">
      <ZoomPanFrame className="pcp-thermal__frame" resetKey={indicationId}>
        <ThermalAnnotator
          imageSrc={imageSrc}
          rois={annotator.rois}
          points={annotator.points}
          activeRoiIndex={annotator.activeRoiIndex}
          onPointerDown={annotator.handlePointerDown}
          onPointerMove={annotator.handlePointerMove}
          onPointerUp={annotator.handlePointerUp}
          imgRef={imgRef}
          interactive={annotator.isAnalyzed}
        />
      </ZoomPanFrame>
      <div className="pcp-thermal__tools">
        <ThermalControls
          mode={annotator.mode}
          onModeChange={annotator.setMode}
          onUndoRoi={annotator.undoRoi}
          onClearRois={annotator.clearRois}
          onClearPoints={annotator.clearPoints}
          isAnalyzing={annotator.isAnalyzing}
          isAnalyzed={annotator.isAnalyzed}
          statusMessage={annotator.statusMessage}
        />
        <ThermalStats rois={annotator.rois} points={annotator.points} />
      </div>
    </div>
  );
}

/* ── Mini-map: source photo + the local fault markers (select + drag) ─────── */

interface PhotoMiniMapProps {
  sourcePhoto: Indication;
  markers: FaultMarker[];
  selectedId: string;
  onSelect: (localId: string) => void;
  /** Local-only: a marker drag reports its new geometry (never a write). */
  onMarkerMove: (localId: string, geometry: GeoJSON.Geometry) => void;
  /** Committed faults to overlay for comparison (type-coloured, click for info). */
  existingFaults: Indication[];
  /** The fault the current link suggestion/selection points at — highlighted. */
  linkTargetId?: string;
  /** Resolved source-photo image src per fault id (for the click-popup thumbnail). */
  faultImages?: Record<string, string>;
  /** Click on an existing fault → open a full-size card at the pointer (parent-rendered
   *  so it can overflow the mini-map, above the classify panel). */
  onFaultClick?: (faultId: string, ev: { clientX: number; clientY: number }) => void;
}

const MARKERS_SRC = 'pcp-markers';
const EXISTING_SRC = 'pcp-existing';

/** Latest inspection day this fault was observed on (for the mini-map popup). */
function latestSeen(ind: Indication): string {
  const obs = deriveObservations(ind);
  const d = obs[obs.length - 1]?.date;
  return d ? String(d).slice(0, 10) : '';
}

/** Committed faults → FeatureCollection for the classify mini-map overlay. */
function existingFaultsFC(faults: Indication[], targetId?: string, images?: Record<string, string>): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: faults
      .filter((f) => f.geometry.type === 'Polygon' || f.geometry.type === 'Point')
      .map((f) => ({
        type: 'Feature' as const,
        geometry: f.geometry,
        properties: {
          id: f.id,
          color: INDICATION_TYPES[f.type]?.color ?? '#888888',
          typeLabel: INDICATION_TYPES[f.type]?.label ?? f.type,
          loc: faultLocationLabel(f),
          date: latestSeen(f),
          img: images?.[f.id] ?? '',
          isTarget: f.id === targetId ? 1 : 0,
        },
      })),
  };
}

function markersFC(markers: FaultMarker[], selectedId: string): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: markers.map((m) => ({
      type: 'Feature' as const,
      geometry: m.geometry,
      properties: {
        localId: m.localId,
        sel: m.localId === selectedId ? 1 : 0,
        // Untyped marker shows in the Photo colour (magenta) — the same look as
        // the source photo's marker on the main map; a typed one takes its
        // fault colour.
        color: m.type ? INDICATION_TYPES[m.type]?.color ?? '#ff00ff' : '#ff00ff',
      },
    })),
  };
}

function PhotoMiniMap({ sourcePhoto, markers, selectedId, onSelect, onMarkerMove, existingFaults, linkTargetId, faultImages, onFaultClick }: PhotoMiniMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef(markers);
  useEffect(() => { markersRef.current = markers; }, [markers]);
  const selectedRef = useRef(selectedId);
  useEffect(() => { selectedRef.current = selectedId; }, [selectedId]);
  const onSelectRef = useRef(onSelect);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);
  const onMoveRef = useRef(onMarkerMove);
  useEffect(() => { onMoveRef.current = onMarkerMove; }, [onMarkerMove]);
  const existingRef = useRef(existingFaults);
  useEffect(() => { existingRef.current = existingFaults; }, [existingFaults]);
  const linkTargetRef = useRef(linkTargetId);
  useEffect(() => { linkTargetRef.current = linkTargetId; }, [linkTargetId]);
  const imagesRef = useRef(faultImages);
  useEffect(() => { imagesRef.current = faultImages; }, [faultImages]);
  const onFaultClickRef = useRef(onFaultClick);
  useEffect(() => { onFaultClickRef.current = onFaultClick; }, [onFaultClick]);

  // Create the map once; reuse across photos.
  useEffect(() => {
    if (!containerRef.current) return;
    const start = getLngLat(sourcePhoto) ?? [0, 0];
    // Orientation mirrors the MAIN map (initial bearing + live rotate sync) so
    // the minimap reads the same way the user left the editor behind the panel.
    const mainMap = useMapStore.getState().map;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: start as [number, number],
      zoom: 20,
      bearing: mainMap?.getBearing() ?? 0,
      attributionControl: false,
      dragRotate: false,
    });
    mapRef.current = map;
    map.addControl?.(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

    const syncBearing = () => map.setBearing(mainMap!.getBearing());
    if (mainMap) mainMap.on?.('rotate', syncBearing);

    const ro = new ResizeObserver(() => map.resize?.());
    if (containerRef.current) ro.observe(containerRef.current);

    // ── Drag the marker under the cursor (local markers only) ──
    let dragId: string | null = null;
    let dragStart: { lng: number; lat: number } | null = null;
    let dragGeom: GeoJSON.Geometry | null = null;
    const setMarkersData = (override?: { localId: string; geom: GeoJSON.Geometry }) => {
      const src = map.getSource(MARKERS_SRC) as mapboxgl.GeoJSONSource | undefined;
      if (!src) return;
      const ms = override
        ? markersRef.current.map((m) => (m.localId === override.localId ? { ...m, geometry: override.geom } : m))
        : markersRef.current;
      src.setData(markersFC(ms, selectedRef.current));
    };
    const onDown = (e: mapboxgl.MapMouseEvent & { features?: GeoJSON.Feature[] }) => {
      const f = e.features && e.features[0];
      const localId = f?.properties?.localId as string | undefined;
      if (!localId) return;
      e.preventDefault();
      onSelectRef.current(localId);
      dragId = localId;
      dragStart = { lng: e.lngLat.lng, lat: e.lngLat.lat };
      dragGeom = markersRef.current.find((m) => m.localId === localId)?.geometry ?? null;
      map.dragPan.disable();
      map.getCanvas().style.cursor = 'grabbing';
    };
    const onMove = (e: mapboxgl.MapMouseEvent) => {
      if (!dragId || !dragStart || !dragGeom) return;
      setMarkersData({ localId: dragId, geom: translateGeometry(dragGeom, e.lngLat.lng - dragStart.lng, e.lngLat.lat - dragStart.lat) });
    };
    const onUp = (e: mapboxgl.MapMouseEvent) => {
      if (!dragId || !dragStart || !dragGeom) return;
      const geom = translateGeometry(dragGeom, e.lngLat.lng - dragStart.lng, e.lngLat.lat - dragStart.lat);
      const id = dragId;
      dragId = null; dragStart = null; dragGeom = null;
      map.dragPan.enable();
      map.getCanvas().style.cursor = '';
      onMoveRef.current(id, geom);
    };

    map.on('style.load', async () => {
      const orthos = useProjectStore.getState().data.orthophotos;
      const overlays = useProjectStore.getState().data.overlays;
      const needSigning = [
        ...orthos.filter((o) => o.visible && o.url && !(o.tileStatus === 'ready' && !!o.tilesetId)).map((o) => o.url),
        ...overlays.filter((o) => o.visible && o.url && !(o.tileStatus === 'ready' && !!o.tilesetId)).map((o) => o.url),
      ];
      const signed = needSigning.length ? await resolveSignedUrls(needSigning) : new Map<string, string>();
      if (mapRef.current !== map) return;

      for (const ortho of orthos) {
        if (!ortho.visible || !ortho.url) continue;
        const srcId = `ortho-src-${ortho.id}`;
        if (map.getSource(srcId)) continue;
        const useTiles = ortho.tileStatus === 'ready' && !!ortho.tilesetId;
        if (useTiles) {
          map.addSource(srcId, {
            type: 'raster', tiles: [buildTileUrl(ortho.tilesetId!)], tileSize: 512,
            bounds: [ortho.bounds.minLng, ortho.bounds.minLat, ortho.bounds.maxLng, ortho.bounds.maxLat] as [number, number, number, number],
            minzoom: ortho.tileMinZoom ?? 10, maxzoom: ortho.tileMaxZoom ?? 22,
          });
        } else {
          map.addSource(srcId, { type: 'image', url: signed.get(ortho.url) ?? ortho.url, coordinates: ortho.coordinates });
        }
        map.addLayer({ id: `ortho-lyr-${ortho.id}`, type: 'raster', source: srcId, paint: { 'raster-opacity': ortho.opacity / 100, 'raster-fade-duration': 0 } });
      }

      for (const ov of overlays) {
        if (!ov.visible || !ov.url) continue;
        const srcId = `overlay-src-${ov.id}`;
        if (map.getSource(srcId)) continue;
        const useTiles = ov.tileStatus === 'ready' && !!ov.tilesetId;
        if (useTiles) {
          const lngs = ov.bounds.map((c) => c[0]); const lats = ov.bounds.map((c) => c[1]);
          map.addSource(srcId, {
            type: 'raster', tiles: [buildTileUrl(ov.tilesetId!)], tileSize: 512,
            bounds: [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)] as [number, number, number, number],
            minzoom: ov.tileMinZoom ?? 10, maxzoom: ov.tileMaxZoom ?? 22,
          });
        } else {
          map.addSource(srcId, { type: 'image', url: signed.get(ov.url) ?? ov.url, coordinates: ov.bounds });
        }
        map.addLayer({ id: `overlay-lyr-${ov.id}`, type: 'raster', source: srcId, paint: { 'raster-opacity': ov.opacity, 'raster-fade-duration': 0 } });
      }

      // Existing committed faults — overlaid UNDER the local markers so the user
      // can compare this photo against the REAL fault positions. Type-coloured,
      // the link-suggestion target highlighted (purple), click for a small
      // type · location · last-seen popup.
      if (!map.getSource(EXISTING_SRC)) {
        map.addSource(EXISTING_SRC, { type: 'geojson', data: existingFaultsFC(existingRef.current, linkTargetRef.current, imagesRef.current) });
        map.addLayer({
          id: 'pcp-existing-fill', type: 'fill', source: EXISTING_SRC,
          // Full type-colour fill like the main map (not dimmed); target a touch stronger.
          paint: { 'fill-color': ['coalesce', ['get', 'color'], '#888888'], 'fill-opacity': ['case', ['==', ['get', 'isTarget'], 1], 0.55, 0.4] },
        });
        map.addLayer({
          id: 'pcp-existing-line', type: 'line', source: EXISTING_SRC,
          paint: {
            'line-color': ['case', ['==', ['get', 'isTarget'], 1], '#7c3aed', '#000000'],
            'line-width': ['case', ['==', ['get', 'isTarget'], 1], 3, 1.5],
            'line-opacity': 0.9,
          },
        });
        map.on('click', 'pcp-existing-fill', (e) => {
          const f = e.features && e.features[0];
          if (!f) return;
          const id = String((f.properties || {}).id ?? '');
          // Hand off to the parent, which renders a full-size card at the pointer
          // (so it can overflow the mini-map and sit above the classify panel).
          if (id) onFaultClickRef.current?.(id, { clientX: e.originalEvent.clientX, clientY: e.originalEvent.clientY });
        });
        map.on('mouseenter', 'pcp-existing-fill', () => { if (!dragId) map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', 'pcp-existing-fill', () => { if (!dragId) map.getCanvas().style.cursor = ''; });
      }

      // Local fault markers — rendered EXACTLY like the main map: type/photo
      // colour fill @0.40 + outline (+ a circle for any Point geometry). The
      // selected marker gets a thicker white outline. Draggable.
      if (!map.getSource(MARKERS_SRC)) {
        map.addSource(MARKERS_SRC, { type: 'geojson', data: markersFC(markersRef.current, selectedRef.current) });
        map.addLayer({
          id: 'pcp-markers-fill', type: 'fill', source: MARKERS_SRC,
          paint: { 'fill-color': ['coalesce', ['get', 'color'], '#ff00ff'], 'fill-opacity': 0.40 },
        });
        map.addLayer({
          id: 'pcp-markers-line', type: 'line', source: MARKERS_SRC,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: {
            'line-color': ['case', ['==', ['get', 'sel'], 1], '#ffffff', '#000000'],
            'line-width': ['case', ['==', ['get', 'sel'], 1], 3, 1.5],
            'line-opacity': 0.95,
          },
        });
        map.addLayer({
          id: 'pcp-markers-circle', type: 'circle', source: MARKERS_SRC,
          filter: ['==', '$type', 'Point'],
          paint: {
            'circle-radius': ['case', ['==', ['get', 'sel'], 1], 9, 7],
            'circle-color': ['coalesce', ['get', 'color'], '#ff00ff'],
            'circle-stroke-width': ['case', ['==', ['get', 'sel'], 1], 3, 1.5],
            'circle-stroke-color': ['case', ['==', ['get', 'sel'], 1], '#ffffff', '#000000'],
          },
        });
        for (const lyr of ['pcp-markers-fill', 'pcp-markers-circle']) {
          map.on('mousedown', lyr, onDown);
          map.on('mouseenter', lyr, () => { if (!dragId) map.getCanvas().style.cursor = 'move'; });
          map.on('mouseleave', lyr, () => { if (!dragId) map.getCanvas().style.cursor = ''; });
        }
        map.on('mousemove', onMove);
        map.on('mouseup', onUp);
      }
    });

    return () => {
      if (mainMap) mainMap.off?.('rotate', syncBearing);
      ro.disconnect();
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recenter when the photo changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const c = getLngLat(sourcePhoto);
    if (c) map.flyTo?.({ center: c, zoom: 20, duration: 350 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourcePhoto.id]);

  // Refresh the markers layer whenever markers / selection change.
  useEffect(() => {
    (mapRef.current?.getSource(MARKERS_SRC) as mapboxgl.GeoJSONSource | undefined)?.setData(markersFC(markers, selectedId));
  }, [markers, selectedId]);

  // Refresh the existing-faults overlay when the committed faults / target change.
  useEffect(() => {
    (mapRef.current?.getSource(EXISTING_SRC) as mapboxgl.GeoJSONSource | undefined)?.setData(existingFaultsFC(existingFaults, linkTargetId, faultImages));
  }, [existingFaults, linkTargetId, faultImages]);

  return <div ref={containerRef} className="pcp-minimap" data-testid="pcp-minimap" />;
}

/* ── Panel ───────────────────────────────────────────────────────────────── */

export default function PhotoClassificationPanel({ onClose }: PhotoClassificationPanelProps) {
  const isReadOnly = useIsReadOnlyViewer();
  const indications = useProjectStore((s) => s.data.indications);
  const addIndication = useProjectStore((s) => s.addIndication);
  const updateIndication = useProjectStore((s) => s.updateIndication);
  const addNotification = useUIStore((s) => s.addNotification);
  const siteId = useProjectStore((s) => s.meta.siteId);
  // For the mini-map fault card — mirror the map's observation selection.
  const indicationViewMode = useMapStore((s) => s.indicationViewMode);
  const visibleDates = useProjectStore((s) => s.visibleDates);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // #2: filter the unclassified-photo queue by inspection YEAR so a fresh import
  // doesn't force re-walking last year's photos. Defaults to the latest year.
  const [yearFilter, setYearFilter] = useState('');
  const didInitYearRef = useRef(false);
  const [cursor, setCursor] = useState(0);
  const [currentVisualSettled, setCurrentVisualSettled] = useState(false);
  // PFC-4B: local fault markers for the current photo + which one is selected.
  const [markers, setMarkers] = useState<FaultMarker[]>([]);
  const [selectedId, setSelectedId] = useState('');
  // #3: full-size info card for an existing fault clicked on the mini-map, at the
  // pointer — rendered at the panel root so it overflows the mini-map / panel.
  const [faultCard, setFaultCard] = useState<{ fault: Indication; x: number; y: number } | null>(null);
  const cardImgRef = useRef<HTMLImageElement>(null);
  const cardNoop = useCallback(() => {}, []);
  // PFC-5: jump-to-photo input value (1-based, synced to the current position).
  const [jumpValue, setJumpValue] = useState('1');
  // PFC-5: previous-session resume marker — captured the first time siteId is
  // available (robust to async project hydration), then frozen so this session's
  // own navigation never changes the resume target.
  const [savedResume, setSavedResume] = useState<SavedResume | null>(null);
  const resumeCapturedRef = useRef(false);
  useEffect(() => {
    if (resumeCapturedRef.current || !siteId) return;
    resumeCapturedRef.current = true;
    setSavedResume(readSavedResume(siteId));
  }, [siteId]);

  const handleVisualSettled = useCallback(() => setCurrentVisualSettled(true), []);
  // Per-marker thermal ROI in a ref keyed by marker localId — the analyzer fires
  // onChange during its own render, so writing to a ref (not setState) avoids the
  // "update a component while rendering another" warning. Read on commit / mount.
  const markerThermal = useRef<Map<string, ThermalAnalysisData>>(new Map());
  const handleThermalReport = useCallback((localId: string, data: ThermalAnalysisData) => {
    markerThermal.current.set(localId, data);
  }, []);

  useEffect(() => {
    if (isReadOnly) onClose();
  }, [isReadOnly, onClose]);

  const totalPhotos = useMemo(
    () => indications.filter((i) => i.type === 'Photo').length,
    [indications],
  );

  // Inspection years present among UNCLASSIFIED photos (newest first).
  const photoYears = useMemo(() => {
    const ys = new Set<string>();
    for (const i of indications) {
      if (i.type !== 'Photo' || i.properties.reviewed === true) continue;
      const d = i.properties.captureDate;
      if (d) ys.add(String(d).slice(0, 4));
    }
    return [...ys].sort((a, b) => b.localeCompare(a));
  }, [indications]);

  // Default the year filter to the LATEST year once photos are known, so opening
  // the panel after an import lands on the new photos (older years are opt-in).
  // Also fall back to All if the selected year runs out of unclassified photos.
  useEffect(() => {
    if (photoYears.length === 0) return;
    if (!didInitYearRef.current) {
      didInitYearRef.current = true;
      setYearFilter(photoYears[0]);
    } else if (yearFilter && !photoYears.includes(yearFilter)) {
      setYearFilter('');
    }
  }, [photoYears, yearFilter]);

  const queue = useMemo<Indication[]>(
    () =>
      indications
        .filter(
          (i) =>
            i.type === 'Photo' &&
            i.properties.reviewed !== true &&
            // Year filter — undated photos always pass (nothing to filter on).
            (!yearFilter || !i.properties.captureDate || String(i.properties.captureDate).slice(0, 4) === yearFilter) &&
            inDateRange(i.properties.captureDate, fromDate, toDate),
        )
        // Chronological by full capture time (createdAt), not the day-only
        // captureDate — a whole campaign ties on the day, which degraded the
        // order to Drive-folder listing order. Undated photos sort last.
        .sort((a, b) => compareCaptureOrder(a.properties, b.properties)),
    [indications, fromDate, toDate, yearFilter],
  );

  useEffect(() => {
    if (cursor > 0 && cursor >= queue.length) setCursor(Math.max(0, queue.length - 1));
  }, [queue.length, cursor]);

  const safeIndex = queue.length === 0 ? 0 : Math.min(cursor, queue.length - 1);
  const current = queue[safeIndex];
  // Clear the per-marker ROI ref the moment the PHOTO changes — in the render
  // phase, BEFORE the analyzer reads `existingThermalData`. Every photo reuses
  // the `m_init` localId for its first marker, so without this a photo's ROI
  // would leak onto the next one.
  const prevPhotoIdRef = useRef<string | undefined>(undefined);
  if (prevPhotoIdRef.current !== current?.id) {
    prevPhotoIdRef.current = current?.id;
    markerThermal.current.clear();
  }
  const lngLat = current ? getLngLat(current) : null;

  const selectedMarker = markers.find((m) => m.localId === selectedId) ?? null;
  const selectedCenter: [number, number] | null = selectedMarker ? geomCenter(selectedMarker.geometry) : null;
  // GRID-ONLY fill source: the marker's real geometry (else the photo point).
  // null unless a module cell answered — the fill effect below writes into
  // the committed indication, and proximity must never write (owner ruling).
  const gridFill = useDerivedFieldsForGeometry(
    selectedMarker?.geometry
      ?? (lngLat ? ({ type: 'Point', coordinates: lngLat } as GeoJSON.Geometry) : null),
  );
  // Old proximity suggestion retained for DISPLAY-only affordances below
  // (link labels) until this panel's full routing; it writes nothing.
  const topoSuggestion = useTopologySuggestion(selectedCenter ?? lngLat);

  // Cross-inspection linking: the nearest already-committed fault to the selected
  // marker, within LINK_SUGGEST_RADIUS_M. Lets the user record this photo as a NEW
  // OBSERVATION of a recurring fault instead of creating a duplicate.
  const nearestFault = useMemo(() => {
    if (!selectedCenter) return null;
    let best: Indication | null = null;
    let bestDist = Infinity;
    for (const ind of indications) {
      if (!FAULT_TYPE_KEYS.has(ind.type)) continue;                 // faults only (never Photo)
      if (ind.id === current?.id) continue;                         // the source photo itself
      if (ind.properties.sourcePhotoId === current?.id) continue;   // our own just-made children
      const c = getLngLat(ind);
      if (!c) continue;
      const d = haversineM(selectedCenter, c);
      if (d < bestDist) { bestDist = d; best = ind; }
    }
    if (!best || bestDist > LINK_SUGGEST_RADIUS_M) return null;
    return { fault: best, dist: bestDist };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCenter, indications, current?.id]);

  // The fault the selected marker is currently linked to (if any) — drives the UI.
  const linkedTarget = selectedMarker?.linkTargetId
    ? indications.find((i) => i.id === selectedMarker.linkTargetId) ?? null
    : null;

  // Committed faults to overlay on the classify mini-map for comparison, plus the
  // fault the current suggestion/selection targets (highlighted on that mini-map).
  const existingFaultsForMap = useMemo(
    () => indications.filter((i) => FAULT_TYPE_KEYS.has(i.type) && i.id !== current?.id && i.properties.sourcePhotoId !== current?.id),
    [indications, current?.id],
  );
  const activeLinkTargetId = selectedMarker?.linkTargetId ?? nearestFault?.fault.id;

  // #4: identify the link target by its LAYOUT LOCATION. Prefer stored topology;
  // if the fault carries none, derive it from the site layout at its position.
  const nearestFaultTopo = useTopologySuggestion(nearestFault ? geomCenter(nearestFault.fault.geometry) : null);
  const linkedTargetTopo = useTopologySuggestion(linkedTarget ? geomCenter(linkedTarget.geometry) : null);
  const nearestLabel = nearestFault
    ? (faultLocationLabel(nearestFault.fault) || topoFieldsLabel(nearestFaultTopo?.fields) || `#${shortId(nearestFault.fault.id)}`)
    : '';
  const linkedLabel = linkedTarget
    ? (faultLocationLabel(linkedTarget) || topoFieldsLabel(linkedTargetTopo?.fields) || `#${shortId(linkedTarget.id)}`)
    : '';

  // Resolved source-photo image per fault, for the mini-map click popup thumbnail.
  const faultImages = useMemo(() => {
    const m: Record<string, string> = {};
    for (const f of existingFaultsForMap) {
      const path =
        f.properties.image ||
        (f.properties.sourcePhotoId ? indications.find((p) => p.id === f.properties.sourcePhotoId)?.properties.image : '') ||
        '';
      if (path) m[f.id] = resolveImageSrc(path);
    }
    return m;
  }, [existingFaultsForMap, indications]);

  const openFaultCard = useCallback((id: string, ev: { clientX: number; clientY: number }) => {
    const fault = indications.find((i) => i.id === id);
    if (!fault) return;
    const halfW = 170;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1920;
    setFaultCard({ fault, x: Math.min(Math.max(ev.clientX, halfW + 8), vw - halfW - 8), y: ev.clientY });
  }, [indications]);

  useEffect(() => {
    if (!faultCard) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFaultCard(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [faultCard]);

  // New photo → one selected local marker at the photo location. The original
  // marker (m_init) can never be removed by "Undo last marker".
  useEffect(() => {
    if (!current) { setMarkers([]); setSelectedId(''); return; }
    // The original marker IS the source photo's geometry — the same rectangle
    // the main map shows. Dragging translates a copy; classifying creates a
    // fault with that shape.
    setMarkers([{ localId: 'm_init', geometry: current.geometry, type: null, topo: {}, createIssue: false }]);
    setSelectedId('m_init');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  // Pre-fill the selected marker's EMPTY topology fields — GRID-ONLY (owner
  // ruling 2026-09-02: proximity values never write; these local topo fields
  // are spread into the committed indication, so this fill IS a write path).
  // gridFill is null unless a module cell answered.
  useEffect(() => {
    if (!gridFill) return;
    setMarkers((ms) => {
      let changed = false;
      const next = ms.map((m) => {
        if (m.localId !== selectedId) return m;
        const topo = { ...m.topo };
        for (const f of TOPO_FIELDS) {
          const sug = gridFill.fields[f.key];
          if (!topo[f.key] && sug) { topo[f.key] = sug as string; changed = true; }
        }
        return { ...m, topo };
      });
      return changed ? next : ms;
    });
  }, [gridFill, selectedId]);

  useEffect(() => {
    setCurrentVisualSettled(!current?.properties.visualImage);
  }, [current?.id, current?.properties.visualImage]);

  // Best-effort preload of the NEXT TWO photos' visual images, deferred + low
  // priority — never steals bandwidth from the current visual (PFC-1E). Uses
  // the same sized URL the pane will render, so the preload actually warms the
  // browser cache entry the navigation reads.
  useEffect(() => {
    const nextPaths = [queue[safeIndex + 1], queue[safeIndex + 2]]
      .map((q) => {
        const p = q?.properties;
        return p?.imageRole === 'visual' ? p.image : p?.visualImage;
      })
      .filter((p): p is string => !!p);
    if (nextPaths.length === 0) return;
    const start = () => {
      for (const path of nextPaths) {
        const src = resolveSizedImageSrc(path);
        if (!src) continue;
        const img = new Image();
        img.decoding = 'async';
        (img as HTMLImageElement & { fetchPriority?: string }).fetchPriority = 'low';
        img.src = src;
      }
    };
    if (currentVisualSettled) { start(); return; }
    const t = setTimeout(start, 1500);
    return () => clearTimeout(t);
  }, [safeIndex, queue, currentVisualSettled]);

  const goPrev = () => setCursor((c) => Math.max(0, c - 1));
  const goNext = () => setCursor((c) => Math.min(queue.length - 1, c + 1));

  // PFC-5: keep the jump box showing the current 1-based position.
  useEffect(() => { setJumpValue(String(safeIndex + 1)); }, [safeIndex]);

  // PFC-5: persist the current photo per project as the user navigates, so the
  // NEXT session can resume here. Best-effort; never blocks the panel; skipped
  // in read-only/share, when the queue is empty, or when siteId is unknown.
  // The FIRST settled position (panel open, photo 1) is intentionally skipped so
  // merely re-opening the panel never overwrites the saved resume target.
  const currentId = current?.id;
  const currentCaptureDate = current?.properties.captureDate ?? null;
  const writeArmedRef = useRef(false);
  useEffect(() => {
    if (isReadOnly || !siteId || !currentId) return;
    if (!writeArmedRef.current) { writeArmedRef.current = true; return; }
    writeSavedResume(siteId, { photoId: currentId, captureDate: currentCaptureDate });
  }, [siteId, currentId, currentCaptureDate, isReadOnly]);

  // PFC-5: where "Resume" jumps — the exact saved photo if it's still queued,
  // else the first remaining photo at/after the saved capture time. null hides
  // the control. Respects the current (date-filtered) queue.
  const resumeTarget = useMemo<number | null>(() => {
    if (!savedResume || queue.length === 0) return null;
    const exact = queue.findIndex((q) => q.id === savedResume.photoId);
    if (exact >= 0) return exact;
    const savedDate = savedResume.captureDate;
    if (!savedDate) return null;
    const fallback = queue.findIndex(
      (q) => typeof q.properties.captureDate === 'string' && q.properties.captureDate >= savedDate,
    );
    return fallback >= 0 ? fallback : null;
  }, [savedResume, queue]);

  // PFC-5: jump to a typed 1-based position (clamped); non-numeric resets to the
  // current position. Uses the same cursor path as Prev/Next — no classification.
  const doJump = useCallback(() => {
    if (queue.length === 0) return;
    const n = parseInt(jumpValue, 10);
    if (!Number.isFinite(n)) { setJumpValue(String(safeIndex + 1)); return; }
    setCursor(Math.min(Math.max(n, 1), queue.length) - 1);
  }, [jumpValue, queue.length, safeIndex]);

  const handleResume = useCallback(() => {
    if (resumeTarget !== null) setCursor(resumeTarget);
  }, [resumeTarget]);

  // ── PFC-4B: markers ──────────────────────────────────────────────────────
  const selectMarker = useCallback((localId: string) => setSelectedId(localId), []);
  const onMarkerMove = useCallback(
    (localId: string, geometry: GeoJSON.Geometry) =>
      setMarkers((ms) => ms.map((m) => (m.localId === localId ? { ...m, geometry } : m))),
    [],
  );
  const setMarkerTopo = (localId: string, field: TopoKey, value: string) =>
    setMarkers((ms) => ms.map((m) => (m.localId === localId ? { ...m, topo: { ...m.topo, [field]: value } } : m)));
  const setMarkerIssue = (localId: string, checked: boolean) =>
    setMarkers((ms) => ms.map((m) => (m.localId === localId ? { ...m, createIssue: checked } : m)));

  const addMarker = () => {
    if (!current) return;
    const d = 0.00002 * markers.length;
    const localId = `m_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    setMarkers((ms) => [
      ...ms,
      // A new marker is a copy of the photo's rectangle, offset so it's visible.
      { localId, geometry: translateGeometry(current.geometry, d, d), type: null, topo: {}, createIssue: false },
    ]);
    setSelectedId(localId);
  };

  // Undo only the LAST-added marker (never the original); pure local state.
  const undoLastMarker = () => {
    if (markers.length <= 1) return;
    const removed = markers[markers.length - 1];
    markerThermal.current.delete(removed.localId);
    const next = markers.slice(0, -1);
    setMarkers(next);
    if (selectedId === removed.localId) setSelectedId(next[next.length - 1].localId);
  };

  // Create every typed marker as a CHILD indication, mark the photo reviewed,
  // and let it fall out of the queue (advance is natural).
  const finish = (finalMarkers: FaultMarker[]) => {
    if (!current) return;
    const now = new Date().toISOString();
    let created = 0;
    let linked = 0;
    finalMarkers.forEach((m, i) => {
      if (!m.type) return;
      const rawTd = markerThermal.current.get(m.localId);
      const td = thermalHasContent(rawTd) ? rawTd : undefined;

      if (m.linkTargetId) {
        // ── Link: append a new OBSERVATION to an existing persistent fault. The
        //    fault keeps its geometry/topology; only this inspection's evidence
        //    (source photo + thermal) is added. ──
        const target = indications.find((ind) => ind.id === m.linkTargetId);
        if (!target) return; // vanished — skip
        const newObs: IndicationObservation = {
          date: current.properties.captureDate,
          sourcePhotoId: current.id,
          ...(current.properties.createdAt ? { capturedAt: current.properties.createdAt } : {}),
          ...(td ? { thermalData: td } : {}),
        };
        const props: Partial<IndicationProperties> = {
          observations: [...deriveObservations(target), newObs],
          // Mirror the LATEST observation into the top-level fields so date
          // filtering + code that reads top-level see the newest inspection.
          ...(current.properties.captureDate ? { captureDate: current.properties.captureDate } : {}),
          sourcePhotoId: current.id,
          ...(td ? { thermalData: td } : {}),
        };
        // Re-observing a RESOLVED fault means it's still present → auto-reopen.
        // Folded into THIS single write (system event, by:null, client-stamped) to
        // avoid a two-writes-to-one-row _version race with the status endpoint; a
        // re-observation isn't a human accountability action.
        if (target.properties.status === 'resolved') {
          props.status = 'open';
          props.statusEvents = [
            ...(target.properties.statusEvents ?? []),
            { status: 'open', at: now, by: null, note: `Re-observed on ${current.properties.captureDate ?? 'new inspection'}` },
          ];
        }
        updateIndication(target.id, { properties: props });
        linked++;
      } else {
        // ── Create: a brand-new fault child (original PFC-4B path) ──
        const topo: Record<string, string> = {};
        for (const f of TOPO_FIELDS) {
          const v = m.topo[f.key];
          if (v) topo[f.key] = v;
        }
        addIndication({
          id: `ind_${Date.now()}_${i}_${Math.random().toString(36).substr(2, 7)}`,
          type: m.type,
          geometry: m.geometry,
          properties: {
            sourcePhotoId: current.id,
            ...(current.properties.captureDate ? { captureDate: current.properties.captureDate } : {}),
            ...topo,
            ...(m.createIssue ? { createIssue: true } : {}),
            ...(td ? { thermalData: td } : {}),
            createdAt: now,
            updatedAt: now,
          },
        });
        created++;
      }
    });
    updateIndication(current.id, { properties: { reviewed: true } });
    const parts: string[] = [];
    if (created) parts.push(`${created} fault${created > 1 ? 's' : ''}`);
    if (linked) parts.push(`${linked} linked`);
    addNotification({ type: 'success', title: `Saved ${parts.join(' · ') || '0 faults'} · photo reviewed` });
  };

  // A fault-type click sets the SELECTED marker's type (never the source Photo's).
  // When every marker is typed, finish: create children + review + advance.
  const handleFaultTypeClick = (type: IndicationType) => {
    if (!selectedId) return;
    // Choosing a type = "this is a NEW fault of this type" → clear any link.
    const next = markers.map((m) => (m.localId === selectedId ? { ...m, type, linkTargetId: undefined } : m));
    if (next.length > 0 && next.every((m) => m.type)) finish(next);
    else setMarkers(next);
  };

  // Link the selected marker to an EXISTING fault (records this photo as a new
  // cross-inspection observation). Adopts the target's type so the marker counts
  // as classified; finishing appends the observation instead of creating a child.
  const linkSelectedTo = (faultId: string, faultType: IndicationType) => {
    if (!selectedId) return;
    const next = markers.map((m) => (m.localId === selectedId ? { ...m, type: faultType, linkTargetId: faultId } : m));
    if (next.length > 0 && next.every((m) => m.type)) finish(next);
    else setMarkers(next);
  };

  // Drop the link on the selected marker (keeps its type → becomes a new fault).
  const unlinkSelected = () => {
    if (!selectedId) return;
    setMarkers((ms) => ms.map((m) => (m.localId === selectedId ? { ...m, linkTargetId: undefined } : m)));
  };

  const handleClose = useCallback(() => { onClose(); }, [onClose]);

  if (isReadOnly) return null;

  // A visual-anchored photo (set had no thermal) shows its primary image in
  // the VISUAL pane; the thermal pane shows the placeholder. Besides the
  // imageRole stamp, a Drive-proxy URL in the PRIMARY slot identifies visual
  // anchoring — thermal anchors are always R2 assets or local blobs — which
  // retrofits markers imported before the stamp existed. Guard: a marker
  // carrying thermalData is a THERMAL whatever its URL shape (ancient imports
  // stored some thermals as Drive URLs), so it keeps the thermal pane + ROI.
  const primaryIsVisual =
    current?.properties.imageRole === 'visual' ||
    (!!current?.properties.image &&
      !current?.properties.thermalData &&
      parseDriveIdFromUrl(current.properties.image) !== null);
  const thermalSrc = !primaryIsVisual && current?.properties.image ? resolveImageSrc(current.properties.image) : '';
  const visualSrc = primaryIsVisual
    ? resolveSizedImageSrc(current?.properties.image)
    : resolveSizedImageSrc(current?.properties.visualImage);
  // Full-res original for the progressive upgrade (w=0 → proxy serves the
  // unresized file). Empty for non-Drive paths, which are already full-res.
  const visualPath = primaryIsVisual ? current?.properties.image : current?.properties.visualImage;
  const visualDriveId = visualPath ? parseDriveIdFromUrl(visualPath) : null;
  const visualFullSrc = visualDriveId ? driveThumbnailUrl(visualDriveId, 0) : '';
  const markerIndex = markers.findIndex((m) => m.localId === selectedId);

  // Mini-map fault card content — year-aware (mirrors the map): resolve photo +
  // thermal from the observation for the current view mode + date filter.
  const cardObs = faultCard ? activeObservation(faultCard.fault, indicationViewMode, new Set(visibleDates)) : null;
  const cardSourcePhoto = cardObs?.sourcePhotoId ? indications.find((i) => i.id === cardObs.sourcePhotoId) : undefined;
  const cardImagePath = (faultCard?.fault.properties.image) || cardSourcePhoto?.properties.image || '';
  const cardImageSrc = cardImagePath ? resolveImageSrc(cardImagePath) : '';
  const cardThermal = cardObs?.thermalData;
  const cardHasThermal = !!cardThermal && (((cardThermal.rois?.length ?? 0) > 0) || ((cardThermal.points?.length ?? 0) > 0));

  return (
    <div className="pcp-overlay" data-testid="photo-classification-panel">
      {faultCard && (
        <>
          <div className="pcp-faultcard__backdrop" onClick={() => setFaultCard(null)} data-testid="pcp-faultcard-backdrop" />
          <div className="pcp-faultcard" style={{ left: faultCard.x, top: faultCard.y }} role="dialog" data-testid="pcp-faultcard">
            <div className="pcp-faultcard__header">
              <span className="pcp-faultcard__type">
                <span className="pcp-faultcard__dot" style={{ background: INDICATION_TYPES[faultCard.fault.type]?.color ?? '#888' }} />
                {INDICATION_TYPES[faultCard.fault.type]?.label ?? faultCard.fault.type}
              </span>
              <button className="pcp-faultcard__close" onClick={() => setFaultCard(null)} title="Close">✕</button>
            </div>
            {cardObs && (
              <div className="pcp-faultcard__badge">
                {indicationViewMode === 'asFound' ? 'As found' : 'Current'}
                {cardObs.date ? ` · ${captureDay(cardObs.date)}` : ''}
              </div>
            )}
            {cardImageSrc
              ? <PhotoSection imageSrc={cardImageSrc} thermalData={cardThermal} hasThermalOverlay={cardHasThermal} thermalImgRef={cardImgRef} noop={cardNoop} />
              : <div className="pcp-faultcard__noimg">No image</div>}
            <div className="pcp-faultcard__meta">
              {faultLocationLabel(faultCard.fault) && <div className="pcp-faultcard__loc">{faultLocationLabel(faultCard.fault)}</div>}
            </div>
          </div>
        </>
      )}
      <div className="pcp-container">
        {/* Header */}
        <div className="pcp-header">
          <div className="pcp-header__left">
            <span className="pcp-header__title">Classify Photos</span>
            <span className="pcp-progress" data-testid="pcp-progress">
              {queue.length > 0 ? `Photo ${safeIndex + 1} / ${queue.length}` : '0 / 0'}
            </span>
            {/* PFC-5: jump-to-photo + resume */}
            <span className="pcp-jump">
              <input
                type="number"
                min={1}
                max={Math.max(1, queue.length)}
                value={jumpValue}
                onChange={(e) => setJumpValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); doJump(); } }}
                disabled={queue.length === 0}
                aria-label="Go to photo number"
                data-testid="pcp-jump-input"
              />
              <button type="button" onClick={doJump} disabled={queue.length === 0} data-testid="pcp-jump-go">
                Go
              </button>
            </span>
            {resumeTarget !== null && (
              <button
                type="button"
                className="pcp-resume"
                onClick={handleResume}
                data-testid="pcp-resume"
              >
                Resume → photo {resumeTarget + 1}
              </button>
            )}
          </div>
          <div className="pcp-header__right">
            {photoYears.length > 1 && (
              <label className="pcp-date-field"><span>Year</span>
                <select value={yearFilter} onChange={(e) => { setYearFilter(e.target.value); setCursor(0); }} data-testid="pcp-year-filter">
                  <option value="">All</option>
                  {photoYears.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </label>
            )}
            <label className="pcp-date-field"><span>From</span>
              <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCursor(0); }} data-testid="pcp-from-date" />
            </label>
            <label className="pcp-date-field"><span>To</span>
              <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setCursor(0); }} data-testid="pcp-to-date" />
            </label>
            <button className="pcp-close-btn" onClick={handleClose} title="Close panel" data-testid="pcp-close">✕</button>
          </div>
        </div>

        <div className="pcp-content-row">
          <button
            className="pcp-nav pcp-nav--left"
            onClick={goPrev}
            disabled={queue.length === 0 || safeIndex <= 0}
            title="Previous photo"
            data-testid="pcp-prev"
          >
            ‹
          </button>
          <div className="pcp-content">
        {queue.length === 0 || !current ? (
          <div className="pcp-empty" data-testid="pcp-empty">
            {totalPhotos === 0 ? (
              <p>No photos to classify. Import photos via the Media panel first.</p>
            ) : (
              <p>No unclassified photos in this date range. Try widening the dates above.</p>
            )}
          </div>
        ) : (
          <>
            <div className="pcp-body">
              <div className="pcp-main">
                <div className="pcp-pics">
                  <div className="pcp-col pcp-col--thermal">
                    <div className="pcp-col__label">Thermal <span className="pcp-col__hint">ROI applies to the selected marker</span></div>
                    {thermalSrc ? (
                      <PhotoThermalAnalyzer
                        key={`${current.id}:${selectedId}`}
                        indicationId={selectedId}
                        imageSrc={thermalSrc}
                        photoRelPath={current.properties.image}
                        existingThermalData={markerThermal.current.get(selectedId)}
                        onReport={handleThermalReport}
                      />
                    ) : (
                      <div className="pcp-placeholder">No thermal image</div>
                    )}
                  </div>

                  <div className="pcp-col pcp-col--visual">
                    <div className="pcp-col__label">Visual <span className="pcp-col__hint">full image · scroll to zoom · drag to pan</span></div>
                    {visualSrc ? (
                      <VisualPane src={visualSrc} fullSrc={visualFullSrc} onSettled={handleVisualSettled} />
                    ) : (
                      <div className="pcp-placeholder">No visual image</div>
                    )}
                  </div>
                </div>

                <div className="pcp-col pcp-col--map">
                  <div className="pcp-col__label">Location <span className="pcp-col__hint">drag the selected marker to the fault</span></div>
                  <PhotoMiniMap sourcePhoto={current} markers={markers} selectedId={selectedId} onSelect={selectMarker} onMarkerMove={onMarkerMove} existingFaults={existingFaultsForMap} linkTargetId={activeLinkTargetId} faultImages={faultImages} onFaultClick={openFaultCard} />
                </div>

                {/* Photo data + editable topology for the selected marker */}
                <div className="pcp-meta">
                  <div className="pcp-meta__data">
                    <span className="pcp-meta__type">Type: <strong>{current.type}</strong></span>
                    {current.properties.captureDate && <span className="pcp-meta__chip"><i>Date</i> {current.properties.captureDate}</span>}
                    {selectedCenter && <span className="pcp-meta__coords">{selectedCenter[1].toFixed(6)}, {selectedCenter[0].toFixed(6)}</span>}
                    <span className="pcp-meta__chip"><i>Marker</i> {markerIndex + 1} / {markers.length}</span>
                  </div>
                  <div className="pcp-topo">
                    {TOPO_FIELDS.map((f) => (
                      <label key={f.key} className="pcp-topo__field">
                        <span>{f.label}</span>
                        <input
                          type="text"
                          className="pcp-topo__input"
                          value={selectedMarker ? (selectedMarker.topo[f.key] ?? topoSuggestion?.fields[f.key] ?? '') : ''}
                          placeholder="—"
                          onChange={(e) => selectedMarker && setMarkerTopo(selectedMarker.localId, f.key, e.target.value)}
                          data-testid={`pcp-topo-${f.key}`}
                        />
                        {f.key === 'module' && topoSuggestion?.moduleSuggestion && (
                          <span className="pcp-topo__suffix">/{topoSuggestion.moduleSuggestion.totalModules}</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="pcp-col pcp-col--faults">
                <div className="pcp-col__label">Classify {markers.length > 1 ? `· marker ${markerIndex + 1}/${markers.length}` : ''}</div>

                {/* Cross-inspection linking: record this photo as a new observation
                    of a nearby recurring fault instead of creating a duplicate. */}
                {(linkedTarget || nearestFault) && (
                  <div
                    className={`pcp-link${linkedTarget ? ' pcp-link--on' : ''}${!linkedTarget && nearestFault && nearestFault.dist > LINK_FAR_WARN_M ? ' pcp-link--far' : ''}`}
                    data-testid="pcp-link"
                  >
                    {linkedTarget ? (
                      <>
                        <span className="pcp-link__label">
                          <span className="pcp-link__glyph">↩</span>
                          Linking to {INDICATION_TYPES[linkedTarget.type]?.label ?? linkedTarget.type} · {linkedLabel}
                          <span className="pcp-link__note"> — adds this photo as a new observation</span>
                        </span>
                        <button className="pcp-link__btn pcp-link__btn--ghost" onClick={unlinkSelected} data-testid="pcp-link-undo">
                          New fault instead
                        </button>
                      </>
                    ) : nearestFault ? (
                      <>
                        <span className="pcp-link__label">
                          Recurring fault {Math.round(nearestFault.dist)} m away
                          {nearestFault.dist > LINK_FAR_WARN_M && (
                            <span className="pcp-link__warn"> — far; confirm it's the same fault</span>
                          )}
                        </span>
                        <button
                          className="pcp-link__btn"
                          onClick={() => linkSelectedTo(nearestFault.fault.id, nearestFault.fault.type)}
                          data-testid="pcp-link-btn"
                        >
                          Link to {INDICATION_TYPES[nearestFault.fault.type]?.label ?? nearestFault.fault.type} · {nearestLabel}
                        </button>
                      </>
                    ) : null}
                  </div>
                )}

                <div className="pcp-faults__grid">
                  {FAULT_TYPES.map((f) => (
                    <button
                      key={f.key}
                      className={`pcp-fault-btn${selectedMarker?.type === f.key ? ' pcp-fault-btn--active' : ''}`}
                      style={{ ['--fault' as string]: f.color } as React.CSSProperties}
                      onClick={() => handleFaultTypeClick(f.key)}
                      data-testid={`pcp-fault-${f.key}`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                <label className="pcp-issue">
                  <input
                    type="checkbox"
                    checked={!!selectedMarker?.createIssue}
                    onChange={(e) => selectedMarker && setMarkerIssue(selectedMarker.localId, e.target.checked)}
                    data-testid="pcp-issue"
                  />
                  Create issue
                </label>

                <div className="pcp-marker-actions">
                  <button className="pcp-add-fault" onClick={addMarker} data-testid="pcp-add-indication">
                    + Add indication
                  </button>
                  <button className="pcp-undo-marker" onClick={undoLastMarker} disabled={markers.length <= 1} data-testid="pcp-undo-marker">
                    Undo last marker
                  </button>
                </div>

                {markers.length > 1 && (
                  <div className="pcp-markerlist" data-testid="pcp-marker-list">
                    <div className="pcp-markerlist__label" data-testid="pcp-marker-count">Markers ({markers.length})</div>
                    {markers.map((m, i) => (
                      <button
                        key={m.localId}
                        className={`pcp-markerlist__chip${m.localId === selectedId ? ' pcp-markerlist__chip--sel' : ''}`}
                        onClick={() => selectMarker(m.localId)}
                        data-testid={`pcp-marker-${m.localId}`}
                      >
                        <span className="pcp-markerlist__dot" style={{ background: m.type ? INDICATION_TYPES[m.type]?.color ?? '#888' : 'transparent', borderColor: m.type ? 'transparent' : '#9ca3af' }} />
                        <span className="pcp-markerlist__text">#{i + 1} {m.type ? (INDICATION_TYPES[m.type]?.label ?? m.type) : 'untyped'}</span>
                        {m.linkTargetId && <span className="pcp-markerlist__link" title="Linked to an existing fault (new observation)">↩</span>}
                      </button>
                    ))}
                  </div>
                )}

                <p className="pcp-faults__hint">Click a type to classify the selected marker. The photo finishes when every marker has a type.</p>
              </div>
            </div>
          </>
        )}
          </div>
          <button
            className="pcp-nav pcp-nav--right"
            onClick={goNext}
            disabled={queue.length === 0 || safeIndex >= queue.length - 1}
            title="Next photo"
            data-testid="pcp-next"
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
}
